// BaremeEvaluationOuvertureSinistre.js
import { Button, Input, Modal, Select, Table, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import axiosInstance from "../../../../variables/api/axiosInstance";
import SinglePageLayout from "../../../../layout/SinglePageLayout";
import TextArea from "antd/es/input/TextArea";
import { toast } from "react-toastify";
import ActionDropdown from "../../../production/components/ActionDropdown";
import { displayNumbers } from "../../../../variables/numbersFunctions";

const BaremeEvaluationOuvertureSinistre = () => {
  const [reload, setReload] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [lignes, setLignes] = useState([]);
  const [loadingBareme, setLoadingBareme] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedLigne, setSelectedLigne] = useState(null);

  const [intitule, setIntitule] = useState("");
  const [nature, setNature] = useState("MIXTE");
  const [prix, setPrix] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    axiosInstance
      .get(`/api/bareme-sinistre`)
      .then((res) => {
        setLignes(res.data.reverse());
        setLoadingBareme(false);
      })
      .catch(() => setLoadingBareme(false));
  }, [reload]);

  const resetForm = () => {
    setIntitule("");
    setNature("MIXTE");
    setPrix("");
    setDescription("");
    setSelectedLigne(null);
    setEditMode(false);
  };

  const onSubmitBareme = () => {
    const payload = {
      intitule,
      nature,
      prix: parseFloat(prix),
      description,
    };

    if (
      !editMode &&
      lignes.some(
        (l) => l.intitule?.toLowerCase() === intitule.trim().toLowerCase()
      )
    ) {
      toast.error("Ce barème existe déjà.");
      return;
    }

    const request = editMode
      ? axiosInstance.put(`/api/bareme-sinistre`, {
        ...payload,
        baremeSinistreId: selectedLigne?.baremeSinistreId, // ✅ CORRECTION ICI
      })
      : axiosInstance.post(`/api/bareme-sinistre`, payload);

    request
      .then(() => {
        toast.success(
          editMode
            ? "Barème modifié avec succès."
            : "Barème ajouté avec succès."
        );
        setOrderModalOpen(false);
        resetForm();
        setReload((prev) => !prev);
      })
      .catch(() => {
        toast.error("Erreur lors de l'enregistrement du barème.");
      });
  };

  const onDeleteBareme = (ligne) => {
    Modal.confirm({
      title: `Voulez-vous supprimer ce barème ?`,
      onOk: () => {
        axiosInstance
          .delete(`/api/bareme-sinistre/${ligne.baremeSinistreId}`)
          .then(() => {
            toast.success("Barème supprimé avec succès");
            setReload((prev) => !prev);
          })
          .catch(() => toast.error("Erreur lors de la suppression."));
      },
    });
  };

  const handleEdit = (ligne) => {
    setEditMode(true);
    setSelectedLigne(ligne);
    setIntitule(ligne.intitule || "");
    setNature(ligne.nature || "MIXTE");
    setPrix(ligne.prix || "");
    setDescription(ligne.description || "");
    setOrderModalOpen(true);
  };

  const columns = [
    {
      title: "Libellé",
      render: (_, record) => `${record.intitule}`,
    },
    {
      title: "Nature",
      dataIndex: "nature",
      render: (_, record) => (
        <Tag color={record.nature === "MATERIEL" ? "blue" : "green"}>
          {record.nature}
        </Tag>
      ),
    },
    {
      title: "Evaluation initiale",
      render: (_, record) =>
        record.prix
          ? `${displayNumbers(record.prix)}`
          : "N/A",
    },
    {
      title: "Reevaluation apres PV ou Rapport",
      dataIndex: "description",
      render: (_, record) => record.description || "N/A",
    },
    {
      title: "Actions",
      render: (_, record) => (
        <ActionDropdown
          actions={[
            {
              key: "edit",
              label: "Modifier",
              icon: <i className="bx bx-edit-alt" />,
              onClick: () => handleEdit(record),
            },
            {
              key: "delete",
              label: "Supprimer",
              icon: <i className="bx bx-trash" />,
              danger: true,
              onClick: () => onDeleteBareme(record),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <SinglePageLayout currentMenu="PARAMETRES_SINISTRES">
      <Typography.Title style={{ fontSize: "26px", color: "#1a1a1a" }}>
        Barème d'évaluation à l'ouverture de dossier sinistre
      </Typography.Title>

      <Button
        icon={<i className="bx bx-plus"></i>}
        type="primary"
        onClick={() => setOrderModalOpen(true)}
      >
        Nouvelle ligne
      </Button>

      <Table
        dataSource={lignes}
        columns={columns}
        rowKey="baremeSinistreId"
        pagination={{ pageSize: 10, showSizeChanger: true }}
        loading={loadingBareme}
      />

      <Modal
        title={editMode ? "Modifier un barème" : "Nouvelle ligne de barème"}
        open={orderModalOpen}
        onOk={onSubmitBareme}
        onCancel={() => {
          setOrderModalOpen(false);
          resetForm();
        }}
        width={700}
      >
        <div className="row">
          <div className="col-6">
            <label>Intitulé</label>
            <Input
              value={intitule}
              onChange={(e) => setIntitule(e.target.value)}
            />
          </div>
          <div className="col-6">
            <label>Evaluation initiale</label>
            <Input
              type="number"
              value={prix}
              onChange={(e) => setPrix(e.target.value)}
            />
          </div>
          <div className="col-12">
            <label>Nature du sinistre</label>
            <Select
              value={nature}
              onChange={setNature}
              style={{ width: "100%" }}
            >
              <Select.Option value="MIXTE">MIXTE</Select.Option>
              <Select.Option value="CORPOREL">CORPOREL</Select.Option>
              <Select.Option value="MATERIEL">MATERIEL</Select.Option>
            </Select>
          </div>
          <div className="col-12">
            <label>Description</label>
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </SinglePageLayout>
  );
};

export default BaremeEvaluationOuvertureSinistre;
